import { buddyClient, qs } from './client';

/**
 * Statistics (H1): usage overview and drill-downs, health, feedback counts, open alerts. Types follow buddy3's
 * `packages/contracts/src/admin.ts` ("Statistics", docs/contracts.md), trimmed to what the hub shows.
 */

export type UsagePeriodName = '24h' | '7d' | '30d' | 'month' | 'prevMonth' | 'year' | 'custom';
export type UsageGroupBy = 'organization' | 'tenant' | 'system' | 'user' | 'model';
export type DrillKind = 'organization' | 'system' | 'tenant';

export interface UsageQuery {
  period?: UsagePeriodName;
  groupBy?: UsageGroupBy;
  /** `period=custom`: inclusive `YYYY-MM-DD`, at most 366 days apart. */
  from?: string;
  to?: string;
  /** Adds `previous` (the equally long period before) to every totals object. */
  compare?: boolean;
  /** IANA zone for the hour and weekday buckets. */
  tz?: string;
}

/** Ratios are fractions 0..1; agents = the pseudo user `monitor` (monitors, scheduled cards). */
export interface UsageTotals {
  tokens: number;
  weightedTokens: number;
  cost: number;
  questions: number;
  requests: number;
  activeUsers: number;
  promptTokens?: number;
  cachedTokens?: number;
  responseTokens?: number;
  agentTokens?: number;
  agentCost?: number;
  agentRequests?: number;
  humanUsers?: number;
  cacheHitRate?: number | null;
  conversations?: number;
  p50DurationMs?: number | null;
  p95DurationMs?: number | null;
  avgSteps?: number | null;
  feedback?: { up: number; down: number; ratio?: number | null };
  failedQueries?: number;
  auditRequests?: number;
  errors?: number;
  errorRate?: number | null;
  previous?: Omit<UsageTotals, 'previous'>;
}

export interface UsagePeriod {
  from: string;
  /** Exclusive end. */
  to: string;
  label?: string;
  yearMonth?: string;
  days?: number;
  previous?: { from: string; to: string; days?: number };
}

export interface UsageGroup extends UsageTotals {
  id: string;
  name: string;
  tenantId?: string;
  tenantName?: string;
  organizationId?: string | null;
  organizationName?: string;
  totals?: UsageTotals;
  /** Organization and system groups only. */
  monthlyLimit?: number | null;
  isUnlimited?: boolean;
  limitScope?: string;
  usedThisMonth?: number;
  projectedThisMonth?: number | null;
  projectedPercentage?: number | null;
  /** `groupBy=user` only. */
  lastActiveAt?: string | null;
  isAgent?: boolean;
}

export interface DailyPoint {
  date: string;
  tokens: number;
  weightedTokens?: number;
  cost: number;
  requests: number;
  questions?: number;
  activeUsers?: number;
  agentTokens?: number;
}

export interface UsageSeries {
  daily?: DailyPoint[];
  /** 24 entries, hour 0..23 in `timezone`. */
  hourly?: { hour: number; requests: number; questions: number }[];
  /** 7 entries, Monday first. */
  weekday?: { weekday: number; name?: string; requests: number; questions: number }[];
  byModel?: { model: string; tokens: number; weightedTokens?: number; cost: number; requests: number }[];
  byThinking?: { thinking: string; requests: number; tokens: number; cost: number }[];
  byKind?: { kind: string; requests: number; errors?: number; avgDurationMs?: number | null }[];
}

/** `GET usage` */
export interface UsageOverview extends UsageSeries {
  period: UsagePeriod;
  groupBy: UsageGroupBy;
  scope?: string;
  timezone?: string;
  totals: UsageTotals;
  groups: UsageGroup[];
}

export interface UsageChild {
  name: string;
  tokens: number;
  weightedTokens?: number;
  cost: number;
  requests: number;
  questions?: number;
  activeUsers?: number;
}

/** `GET usage/{organization|system|tenant}/{id}` */
export interface UsageDetail extends UsageSeries {
  id: string;
  name: string;
  period: UsagePeriod;
  timezone?: string;
  totals: UsageTotals;
  daily: DailyPoint[];
  byUser: {
    userCode: string;
    name?: string;
    tokens: number;
    cost: number;
    requests: number;
    questions?: number;
    lastActiveAt?: string | null;
    isAgent?: boolean;
  }[];
  bySystem?: (UsageChild & { systemId: string })[];
  byTenant?: (UsageChild & { tenantId: string })[];
  recentConversations?: {
    id?: string;
    title: string;
    userCode?: string;
    systemName?: string;
    systemId?: string;
    updatedAt: string;
    messageCount: number;
  }[];
  monthlyLimit?: number | null;
  isUnlimited?: boolean;
  usedThisMonth?: number;
  projectedThisMonth?: number | null;
}

export interface SystemHealth {
  id: string;
  name: string;
  tenantId: string;
  environment?: string;
  enabled: boolean;
  available: boolean;
  status: 'ok' | 'degraded' | 'down' | 'disabled' | (string & {});
  error?: string | null;
  workserverVersion?: string;
}

/** The feedback triage counts (`chat/feedback/stats?scope=all`). */
export interface FeedbackStats {
  total: number;
  up: number;
  down: number;
  handled?: number;
  unhandled: number;
  last7Days?: number;
}

/** `monitors/alerts` (open by default). */
export interface MonitorAlert {
  id: string;
  monitorId: string;
  tenantId: string;
  systemId: string;
  entityKey: string;
  status: string;
  openedAt: string;
  lastSeenAt?: string | null;
}

/** The statistics reads of one environment's nyce.buddy. */
export const buddyFor = (target: string) => {
  const { get } = buddyClient(target);
  return {
    usage: (q: UsageQuery = {}) => get<UsageOverview>(`/usage${qs({ ...q })}`),
    detail: (kind: DrillKind, id: string, q: UsageQuery = {}) =>
      get<UsageDetail>(`/usage/${kind}/${encodeURIComponent(id)}${qs({ ...q, groupBy: undefined })}`),
    health: () => get<SystemHealth[]>('/health'),
    feedbackStats: () => get<FeedbackStats>('/feedback/stats?scope=all'),
    alerts: (q: { status?: string; limit?: number } = {}) => get<MonitorAlert[]>(`/monitors/alerts${qs(q)}`),
  };
};
